import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaPlus, FaMinus } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import { wishesService } from '../../services/wishesService';
import { festivalService } from '../../services/festivalService';
import { specialDayService } from '../../services/specialDayService';
import FestivalDetailModal from './FestivalDetailModal';
import ModeSelectionModal from './ModeSelectionModal';
import LoadingSpinner from '../common/LoadingSpinner';
import smileyIcon from '../../assets/smiley.svg';
import { gregorianToMalayalam, getMalayalamWeekdays, getMalayalamMonthDays } from '../../utils/malayalamCalendar';
import { gregorianToTamil, getTamilWeekdays, getTamilMonthDays } from '../../utils/tamilCalendar';
import './Calendar.css';


const Calendar = () => {
  const { t } = useTranslation();
  const { selectedLanguage } = useLanguage();
  const navigate = useNavigate();
  const [wishesMedia, setWishesMedia] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [specialDays, setSpecialDays] = useState([]);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [modeModal, setModeModal] = useState(null);

  useEffect(() => {
    fetchWishesMedia();
    fetchFestivals();
    fetchSpecialDays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage, currentDate]);

  const fetchWishesMedia = async () => {
    setLoading(true);
    try {
      const response = await wishesService.getWishesGallery();
      setWishesMedia(response.data);
    } catch (error) {
      console.error('Error fetching wishes media:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFestivals = async () => {
    try {
      const response = await festivalService.getFestivals();
      setFestivals(response.data || []);
    } catch (error) {
      console.error('Error fetching festivals:', error);
    }
  };

  const fetchSpecialDays = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await specialDayService.getSpecialDays({
        year,
        month,
        language: selectedLanguage
      });
      setSpecialDays(response.data || []);
    } catch (error) {
      console.error('Error fetching special days:', error);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Get all days in the current month (Sunday first)
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // Sunday = 0

    const days = [];

    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  // Check if a day is Sunday (holiday)
  const isSunday = (day) => {
    if (!day) return false;
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return date.getDay() === 0;
  };

  // Check if a date has wishes media
  const getMediaForDate = (day) => {
    if (!day) return [];
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateStr = `${year}-${month}-${dayStr}`;

    return wishesMedia.filter(media => {
      if (!media.dates || media.dates.length === 0) return false;
      return media.dates.some(mediaDate => {
        const mediaDateStr = new Date(mediaDate).toISOString().split('T')[0];
        return mediaDateStr === dateStr;
      });
    });
  };

  // Handle date click
  const handleDateClick = (day) => {
    if (!day) return;

    // Create the date string for the clicked day
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const selectedDateStr = `${year}-${month}-${dayStr}`;

    // Check if there's already media for this date
    const mediaForDate = getMediaForDate(day);

    if (mediaForDate.length > 0) {
      const media = mediaForDate[0];
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const thumbUrl = media.thumbnailUrl
        ? (media.thumbnailUrl.startsWith('http') ? media.thumbnailUrl : `${baseURL}${media.thumbnailUrl}`)
        : (media.mediaUrl && media.mediaUrl.startsWith('http') ? media.mediaUrl : `${baseURL}${media.mediaUrl}`);
      setModeModal({
        mediaId: media._id,
        occasionName: media.occasionName || '',
        thumbnailUrl: thumbUrl
      });
    } else {
      // No media for this date, navigate to wishes page with date filter
      navigate('/wishes', {
        state: { selectedDate: selectedDateStr },
        search: `?date=${selectedDateStr}`
      });
    }
  };

  // Check if Malayalam or Tamil calendar should be shown
  const showMalayalamCalendar = selectedLanguage === 'ml';
  const showTamilCalendar = selectedLanguage === 'ta';

  // Get week days based on language
  const weekDays = showMalayalamCalendar
    ? getMalayalamWeekdays()
    : showTamilCalendar
    ? getTamilWeekdays()
    : [
        t('day_sun'),
        t('day_mon'),
        t('day_tue'),
        t('day_wed'),
        t('day_thu'),
        t('day_fri'),
        t('day_sat')
      ];

  // Always use Gregorian calendar as base
  const days = getDaysInMonth();
  const currentMonthIndex = currentDate.getMonth();
  const monthNames = [
    t('month_january'),
    t('month_february'),
    t('month_march'),
    t('month_april'),
    t('month_may'),
    t('month_june'),
    t('month_july'),
    t('month_august'),
    t('month_september'),
    t('month_october'),
    t('month_november'),
    t('month_december')
  ];
  const monthName = monthNames[currentMonthIndex];
  const currentYear = currentDate.getFullYear();

  // Get Malayalam calendar info if needed
  let malayalamMonthName = '';
  let malayalamYear = '';
  if (showMalayalamCalendar) {
    // Check all days in the month to find unique Malayalam months
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const malayalamMonthsFound = [];

    // Check first and last day of the Gregorian month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month, daysInMonth);

    const firstMalayalamDate = gregorianToMalayalam(firstDay);
    const lastMalayalamDate = gregorianToMalayalam(lastDay);

    // Add first month
    malayalamMonthsFound.push({
      monthName: firstMalayalamDate.monthName,
      monthIndex: firstMalayalamDate.monthIndex,
      year: firstMalayalamDate.year
    });

    // If first and last days are in different Malayalam months, add last month too
    if (firstMalayalamDate.monthIndex !== lastMalayalamDate.monthIndex) {
      malayalamMonthsFound.push({
        monthName: lastMalayalamDate.monthName,
        monthIndex: lastMalayalamDate.monthIndex,
        year: lastMalayalamDate.year
      });
    }

    // Join month names in order
    malayalamMonthName = malayalamMonthsFound.map(m => m.monthName).join(' - ');

    // Get unique years
    const uniqueYears = [...new Set(malayalamMonthsFound.map(m => m.year))];
    malayalamYear = uniqueYears.join('-');
  }

  // Get Tamil calendar info if needed
  let tamilMonthName = '';
  let tamilYear = '';
  if (showTamilCalendar) {
    // Check all days in the month to find unique Tamil months
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const tamilMonthsFound = [];

    // Check first and last day of the Gregorian month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month, daysInMonth);

    const firstTamilDate = gregorianToTamil(firstDay);
    const lastTamilDate = gregorianToTamil(lastDay);

    // Add first month
    tamilMonthsFound.push({
      monthName: firstTamilDate.monthName,
      monthIndex: firstTamilDate.monthIndex,
      year: firstTamilDate.year
    });

    // If first and last days are in different Tamil months, add last month too
    if (firstTamilDate.monthIndex !== lastTamilDate.monthIndex) {
      tamilMonthsFound.push({
        monthName: lastTamilDate.monthName,
        monthIndex: lastTamilDate.monthIndex,
        year: lastTamilDate.year
      });
    }

    // Join month names in order
    tamilMonthName = tamilMonthsFound.map(m => m.monthName).join(' - ');

    // Get unique years
    const uniqueYears = [...new Set(tamilMonthsFound.map(m => m.year))];
    tamilYear = uniqueYears.join('-');
  }

  // Get festivals for the current month
  const getCurrentMonthFestivals = () => {
    return festivals.filter(festival => {
      if (!festival.date) return false;
      const festivalDate = new Date(festival.date);
      return festivalDate.getMonth() === currentDate.getMonth() &&
             festivalDate.getFullYear() === currentDate.getFullYear();
    });
  };

  const monthFestivals = getCurrentMonthFestivals();

  // Get the first festival for display (if any)
  const displayFestival = monthFestivals.length > 0 ? monthFestivals[0] : null;

  // Helper function to get localized text from Map field
  const getLocalizedText = (mapField, fallbackLang = 'en') => {
    if (!mapField) return '';
    // mapField is a Map object from MongoDB
    if (typeof mapField === 'object') {
      return mapField[selectedLanguage] || mapField[fallbackLang] || Object.values(mapField)[0] || '';
    }
    return mapField;
  };

  // Helper function to get text ONLY in the selected language (no fallback)
  const getLocalizedTextStrict = (mapField) => {
    if (!mapField) return '';
    if (typeof mapField === 'object') {
      // Only return if the selected language exists, no fallback
      return mapField[selectedLanguage] || '';
    }
    return mapField;
  };

  // Helper function to validate if city data is properly formatted (not character-split)
  const isValidCityData = (cityField) => {
    if (!cityField || typeof cityField !== 'object') return false;

    // Check if keys are language codes (like 'en', 'or', 'ta') instead of numbers
    const keys = Object.keys(cityField);
    if (keys.length === 0) return false;

    // If keys are numeric (0, 1, 2...), it's the invalid character-split format
    const hasNumericKeys = keys.every(key => !isNaN(key));
    if (hasNumericKeys) return false;

    // Check if at least one value is a proper string (length > 2)
    const values = Object.values(cityField);
    return values.some(val => val && typeof val === 'string' && val.length > 2);
  };

  // Check if a day has media (for smiley marker)
  const hasMedia = (day) => {
    if (!day) return false;
    return getMediaForDate(day).length > 0;
  };

  return (
    <div className="calendar-container" data-lang={selectedLanguage}>
      <div className="calendar-card">
        {/* Month/Year Header with Navigation - Moved to Top */}
        <div className="month-header">
          <button className="nav-arrow nav-arrow-left" onClick={() => navigateMonth(-1)}>
            <FaChevronLeft />
          </button>

          <div className="month-year-display">
            <span className="month-name">{monthName} / {currentYear}</span>
            {showMalayalamCalendar && (
              <div className="malayalam-month-subheader">{malayalamMonthName} / {malayalamYear}</div>
            )}
            {showTamilCalendar && (
              <div className="malayalam-month-subheader">{tamilMonthName} / {tamilYear}</div>
            )}
          </div>

          <button className="nav-arrow nav-arrow-right" onClick={() => navigateMonth(1)}>
            <FaChevronRight />
          </button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <LoadingSpinner size={50} color="#244A95" text={t('loading')} />
          </div>
        ) : (
          <>
            {/* Calendar Grid */}
            <div className="calendar-grid">
              {/* Week day headers */}
              {weekDays.map((day, index) => (
                <div
                  key={day}
                  className={`calendar-weekday ${index === 0 ? 'sunday' : ''} ${selectedLanguage !== 'en' ? 'non-english' : ''}`}
                >
                  {day}
                </div>
              ))}

              {/* Calendar days */}
              {days.map((day, index) => {
                if (!day) {
                  return <div key={index} className="calendar-day empty"></div>;
                }

                const today = new Date();
                const isToday = day === today.getDate() &&
                               currentDate.getMonth() === today.getMonth() &&
                               currentDate.getFullYear() === today.getFullYear();
                const mediaOnDate = getMediaForDate(day);
                const hasMediaOnDate = mediaOnDate.length > 0;
                const dayIsSunday = isSunday(day);
                const showSmiley = hasMedia(day);
                const isOddDay = day % 2 !== 0;

                // Get Malayalam date if showing Malayalam calendar
                let malayalamDayNumber = null;
                if (showMalayalamCalendar) {
                  const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                  const malayalamDateInfo = gregorianToMalayalam(dayDate);
                  malayalamDayNumber = malayalamDateInfo.date;
                }

                // Get Tamil date if showing Tamil calendar
                let tamilDayNumber = null;
                if (showTamilCalendar) {
                  const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                  const tamilDateInfo = gregorianToTamil(dayDate);
                  tamilDayNumber = tamilDateInfo.date;
                }

                return (
                  <div
                    key={index}
                    className={`calendar-day ${isToday ? 'today' : ''} ${hasMediaOnDate ? 'has-event' : ''} ${dayIsSunday ? 'sunday' : ''} ${isOddDay ? 'odd-day' : 'even-day'}`}
                    onClick={() => handleDateClick(day)}
                  >
                    {showSmiley && (
                      <img
                        src={smileyIcon}
                        alt="Event"
                        className="festival-smiley"
                      />
                    )}
                    {showMalayalamCalendar && malayalamDayNumber && (
                      <span className="malayalam-day-number">{malayalamDayNumber}</span>
                    )}
                    {showTamilCalendar && tamilDayNumber && (
                      <span className="malayalam-day-number">{tamilDayNumber}</span>
                    )}
                    <span className={`day-number ${dayIsSunday ? 'sunday-number' : ''}`}>{day}</span>
                  </div>
                );
              })}
            </div>

            {/* Special Days Accordion */}
            {(() => {
              // Check if we have special days with valid items in the SELECTED language only
              const hasValidSpecialDays = specialDays.length > 0 &&
                specialDays[0] &&
                specialDays[0].items &&
                specialDays[0].items.length > 0 &&
                specialDays[0].items.some(item => getLocalizedTextStrict(item.text));

              if (!hasValidSpecialDays) return null;

              // Only show pills that have text in the selected language (no fallback)
              const validItems = specialDays[0].items.filter(item => getLocalizedTextStrict(item.text));

              return (
                <div className="special-days-accordion">
                  <button
                    className="accordion-header"
                    onClick={() => setIsAccordionOpen(!isAccordionOpen)}
                  >
                    <h3 className="accordion-title">
                      {getLocalizedText(specialDays[0].title) || 'Special Days'}
                    </h3>
                    <span className="accordion-icon">
                      {isAccordionOpen ? <FaMinus /> : <FaPlus />}
                    </span>
                  </button>
                  {isAccordionOpen && (
                    <div className="accordion-content">
                      <div className="special-days-pills">
                        {validItems.map((item, index) => (
                          <span key={index} className="special-day-pill">
                            {getLocalizedTextStrict(item.text)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Festival Notice - Moved Below Calendar */}
            <div className={`festival-notice ${selectedLanguage !== 'en' ? 'non-english' : ''}`}>
              <span className="notice-text">{t('click_smiley_text')} </span>
              <img src={smileyIcon} alt="Festival" className="notice-smiley" />
              <span className="notice-text"> {t('for_greetings_text')}</span>
            </div>
          </>
        )}
        
        {/* Festival Details Section - Shows monthly festival story/info */}
        {displayFestival && (
          <div className="festival-details-section">
            <div className="festival-detail-card">
              <h3 className="festival-title">
                {getLocalizedText(displayFestival.name)}
                {isValidCityData(displayFestival.city) && getLocalizedText(displayFestival.city) && (
                  <>
                    {' / '}
                    <span className="festival-city">
                      <FaMapMarkerAlt className="location-icon" />
                      {getLocalizedText(displayFestival.city)}
                    </span>
                  </>
                )}
              </h3>
              <div className="festival-info">
                <div className="festival-illustration">
                  {displayFestival.images && displayFestival.images.length > 0 ? (
                    <img
                      src={displayFestival.images[0]}
                      alt={getLocalizedText(displayFestival.name)}
                      style={{ width: '100%', maxWidth: '300px', height: '100px', borderRadius: '8px' }}
                    />
                  ) : (
                    <div style={{ width: '100px', height: '100px', background: 'linear-gradient(135deg, #244a95,rgb(21, 55, 122))', borderRadius: '8px' }}></div>
                  )}
                </div>
                <p className="festival-description">
                  {getLocalizedText(displayFestival.description)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedDate?.selectedFestival && (
        <FestivalDetailModal
          festival={selectedDate.selectedFestival}
          onClose={() => setSelectedDate(null)}
          selectedLanguage={selectedLanguage}
        />
      )}

      {modeModal && (
        <ModeSelectionModal
          isOpen={true}
          onClose={() => setModeModal(null)}
          onSelectAuto={() => {
            setModeModal(null);
            navigate(`/wishes/auto/${modeModal.mediaId}`);
          }}
          onSelectEdit={() => {
            setModeModal(null);
            navigate(`/wishes/editor/${modeModal.mediaId}`);
          }}
          occasionName={modeModal.occasionName}
          thumbnailUrl={modeModal.thumbnailUrl}
        />
      )}
    </div>
  );
};

export default Calendar;
